import { safeProjectPath } from '@mio/shared';

interface SourceSelectionOptions {
  onExcluded?: (paths: string[]) => void;
}

/** Read selected source only; excluded paths are reported without reading their contents. */
export async function readFieldSources(files: File[], options: SourceSelectionOptions = {}) {
  const excluded: string[] = [];
  const paths = new Set<string>();
  const selected = files.flatMap((file) => {
    const relative = file.webkitRelativePath;
    const path = relative ? relative.split('/').slice(1).join('/') : file.name;
    if (
      !path ||
      path.startsWith('/') ||
      path.includes('\\') ||
      // eslint-disable-next-line no-control-regex
      /[\u0000-\u001f\u007f]/.test(path) ||
      path.split('/').some((part) => part === '..' || part === '.' || !part)
    )
      throw new Error(`Unsafe relative project path: ${path}`);
    if (
      !safeProjectPath(path) ||
      /(^|\/)(id_rsa|id_ed25519|\.cache|\.local-state|\.mypy_cache|\.ruff_cache|\.DS_Store|[^/]+\.py[co])($|\/)/i.test(
        path,
      )
    ) {
      excluded.push(path);
      return [];
    }
    if (path.length > 240) throw new Error(`${path} exceeds the 240-character path limit.`);
    if (paths.has(path))
      throw new Error(`Duplicate path: ${path}. Choose a project folder to preserve directories.`);
    paths.add(path);
    return [{ file, path }];
  });
  options.onExcluded?.(excluded.sort());
  if (!selected.length)
    throw new Error(
      'No uploadable source files remain. Choose a project folder containing UTF-8 source.',
    );
  if (selected.length > 120)
    throw new Error('Choose at most 120 source files after excluding private and generated files.');
  if (selected.reduce((total, { file }) => total + file.size, 0) > 1_200_000)
    throw new Error(
      'The selected source exceeds the 1.2 MB upload limit. Choose a smaller review.',
    );
  const sources = await Promise.all(
    selected.map(async ({ file, path }) => {
      if (file.size > 200_000) throw new Error(`${path} exceeds the 200 KB file limit.`);
      let content: string;
      try {
        content = new TextDecoder('utf-8', { fatal: true }).decode(await file.arrayBuffer());
      } catch {
        throw new Error(
          `${path} is not a UTF-8 text file. Workbench source uploads do not support binary assets; export a text-source folder.`,
        );
      }
      if (content.includes('\0'))
        throw new Error(
          `${path} contains binary content. Workbench source uploads do not support binary assets; export a text-source folder.`,
        );
      return { path, content };
    }),
  );
  if (new TextEncoder().encode(JSON.stringify(sources)).byteLength > 1_200_000)
    throw new Error(
      'Source including paths and JSON encoding exceeds 1.2 MB. Choose a smaller review.',
    );
  return sources;
}
